import { conjugatePresentFormII_Salim_fromRow } from './arPresentFormII_salim.js';
import { conjugatePresentFormIII_Salim_fromRow } from './arPresentFormIII_salim.js';

const FATHA = '\u064E';
const DAMMA = '\u064F';
const KASRA = '\u0650';
const SUKUN = '\u0652';
const SHADDA = '\u0651';
const MEEM = '\u0645';
const TEH = '\u062A';
const TEH_MARBUTA = '\u0629';
const WAW = '\u0648';
const NUN = '\u0646';
const ALEF = '\u0627';
const YA = '\u064A';
const SEEN = '\u0633';
const HUWA = '\u0647\u0648';
const HARAKAT_RE = /[\u064B-\u065F]/g;
const LETTER_RE = /[\u0621-\u064A]/;

const stripHarakat = (value) => (value || '').replace(HARAKAT_RE, '');

const normalizeBinyan = (value) => {
  const match = String(value || '').match(/\d+/);
  return match ? match[0] : '';
};

const getRootLetters = (row) => {
  const root = stripHarakat((row['שורש'] || row.root || '').trim()).replace(/[^\u0621-\u064A]/g, '');
  if (root.length !== 3) return null;
  return [root[0], root[1], root[2]];
};

const getHuwaForm = (conjugations) => {
  if (!conjugations) return null;
  const huwaEntry = Object.entries(conjugations).find(([pronoun]) => stripHarakat(pronoun) === HUWA);
  return huwaEntry ? huwaEntry[1] : null;
};

const splitIntoClusters = (word) => {
  const clusters = [];
  for (const char of word || '') {
    if (LETTER_RE.test(char)) {
      clusters.push(char);
    } else if (clusters.length > 0) {
      clusters[clusters.length - 1] += char;
    }
  }
  return clusters;
};

const getClusterLetter = (cluster) => {
  const match = (cluster || '').match(LETTER_RE);
  return match ? match[0] : '';
};

const buildCluster = (letter, { keepShadda = false, vowel = '' } = {}) => {
  let cluster = letter;
  if (keepShadda) cluster += SHADDA;
  if (vowel) cluster += vowel;
  return cluster;
};

const setClusterVowel = (cluster, vowel) => {
  const letter = getClusterLetter(cluster);
  if (!letter) return cluster;
  return buildCluster(letter, { keepShadda: cluster.includes(SHADDA), vowel });
};

const clearClusterVowel = (cluster) => {
  const letter = getClusterLetter(cluster);
  if (!letter) return cluster;
  return buildCluster(letter, { keepShadda: cluster.includes(SHADDA) });
};

const buildDirectFuture = (binyan, [r1, r2, r3]) => {
  switch (binyan) {
    case '4':
      return YA + DAMMA + r1 + SUKUN + r2 + KASRA + r3 + DAMMA;
    case '5':
      return YA + FATHA + TEH + FATHA + r1 + FATHA + r2 + SHADDA + FATHA + r3 + DAMMA;
    case '6':
      return YA + FATHA + TEH + FATHA + r1 + FATHA + ALEF + r2 + FATHA + r3 + DAMMA;
    case '7':
      return YA + FATHA + NUN + SUKUN + r1 + FATHA + r2 + KASRA + r3 + DAMMA;
    case '8':
      return YA + FATHA + r1 + SUKUN + TEH + FATHA + r2 + KASRA + r3 + DAMMA;
    case '9':
      return YA + FATHA + r1 + SUKUN + r2 + FATHA + r3 + SHADDA + DAMMA;
    case '10':
      return YA + FATHA + SEEN + SUKUN + TEH + FATHA + r1 + SUKUN + r2 + KASRA + r3 + DAMMA;
    default:
      return null;
  }
};

const BINYAN_CONFIGS = {
  '2': {
    futureBuilder: (row) => getHuwaForm(conjugatePresentFormII_Salim_fromRow(row)),
    rootIndexesAfterPrefix: [0, 1, 2],
  },
  '3': {
    futureBuilder: (row) => getHuwaForm(conjugatePresentFormIII_Salim_fromRow(row)),
    rootIndexesAfterPrefix: [0, 2, 3],
  },
  '4': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('4', rootLetters),
    rootIndexesAfterPrefix: [0, 1, 2],
  },
  '5': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('5', rootLetters),
    rootIndexesAfterPrefix: [1, 2, 3],
  },
  '6': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('6', rootLetters),
    rootIndexesAfterPrefix: [1, 3, 4],
  },
  '7': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('7', rootLetters),
    rootIndexesAfterPrefix: [1, 2, 3],
  },
  '8': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('8', rootLetters),
    rootIndexesAfterPrefix: [0, 2, 3],
  },
  '9': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('9', rootLetters),
    rootIndexesAfterPrefix: [0, 1, 2],
  },
  '10': {
    futureBuilder: (_, rootLetters) => buildDirectFuture('10', rootLetters),
    rootIndexesAfterPrefix: [2, 3, 4],
  },
};

const validateRootIndexes = (clusters, rootLetters, indexes) => (
  indexes.every((index, position) => clusters[index] && getClusterLetter(clusters[index]) === rootLetters[position])
);

const buildBaseParticiple = (stemClusters, rootIndexesAfterPrefix, middleVowel) => {
  const clusters = [MEEM + DAMMA, ...stemClusters];
  const shiftedIndexes = rootIndexesAfterPrefix.map((index) => index + 1);
  const middleIndex = shiftedIndexes[1];
  const lastIndex = shiftedIndexes[2];

  clusters[middleIndex] = setClusterVowel(clusters[middleIndex], middleVowel);
  clusters[lastIndex] = clearClusterVowel(clusters[lastIndex]);

  return clusters.join('');
};

const inflectBaseParticiple = (base) => ({
  masculine: base,
  feminine: `${base}${FATHA}${TEH_MARBUTA}`,
  plural: `${base}${DAMMA}${WAW}${NUN}${FATHA}`,
  pluralFeminine: `${base}${FATHA}${ALEF}${TEH}`,
});

export function buildParticiplesFromRowNonFormI(row) {
  if (!row) return null;

  const binyan = normalizeBinyan(row['בניין'] || row.binyan || '');
  const config = BINYAN_CONFIGS[binyan];
  if (!config) return null;

  const rootLetters = getRootLetters(row);
  if (!rootLetters) return null;

  const futureForm = config.futureBuilder(row, rootLetters);
  if (!futureForm) return null;

  const stemClusters = splitIntoClusters(futureForm).slice(1);
  if (stemClusters.length === 0) return null;
  if (!validateRootIndexes(stemClusters, rootLetters, config.rootIndexesAfterPrefix)) return null;

  const activeBase = buildBaseParticiple(stemClusters, config.rootIndexesAfterPrefix, KASRA);
  const passiveBase = buildBaseParticiple(stemClusters, config.rootIndexesAfterPrefix, FATHA);

  return {
    active: inflectBaseParticiple(activeBase),
    passive: inflectBaseParticiple(passiveBase),
    root: rootLetters.join(''),
  };
}
